import React, { useState, useEffect, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import { withFocusable } from '../../spatial';
import { ParentNodeContextConsumer } from './parent-node-context.jsx';
import gsap, { ScrollToPlugin } from 'gsap/all';
import spatialNavigation from '../../spatial/spatialNavigation';
import { createUUID } from '../../utils/createUUID';
import { PerformanceEnhancerContext } from '../../performance-enabler/performance-enabler.jsx';
import debounce from 'lodash/debounce';

gsap.registerPlugin(ScrollToPlugin);

/**
 * Antares Focusable is the main component in focus management.
 * 
 */

let spatial = spatialNavigation;
if (window.spatial !== null && window.spatial !== undefined) {
    spatial = window.spatial;
} else {
    window.spatial = spatial;
}

const AntaresFocusable = (props) => {

    const onBecameFocused = ({ width, height, x, y, top, left, node }, { prop1, prop2 }, { event, other }) => {
        props.onFocus(props.index, node, spatial.getLastDirection());
    }

    const onBecameBlurred = ({ width, height, x, y, top, left, node }, { prop1, prop2 }, { event, other }) => {
        props.onBlur(props.index, node, spatial.getLastDirection());
    }

    const onEnterPress = (node) => {
        props.onEnterDown(props.index, node, spatial.getLastDirection());
    }

    return (
        <ParentNodeContextConsumer>
            {
                params => (
                    <Focusable
                        focusedClassname={props.focusedClassname}
                        classname={props.classname}
                        onBecameFocused={onBecameFocused}
                        onBecameBlurred={onBecameBlurred}
                        forgetLastFocusedChild={!props.retainLastFocus}
                        onEnterPress={onEnterPress}
                        focusKey={props.focusableId ? props.focusableId : `antares-focusable-${createUUID()}`}
                        stealFocus={props.forceFocus}
                        parentNodeId={params.parentNodeId}
                        parentInternalNodeId={params.parentInternalNodeId}
                        onViewportEnter={props.onViewportEnter}
                        onViewportExit={props.onViewportExit}
                        type={params.type}
                        scrollOffset={props.scrollOffset}
                        fixed={params.fixed}
                        animationDuration={params.animationDuration}
                        index={props.index}
                        focusable={params.isFocusable ? params.isFocusable : props.isFocusable}
                        remainInFocus={params.remainInFocus}
                        gridAnimationDelay={params.gridAnimationDelay}
                    >
                        {props.children}
                    </Focusable>
                )
            }
        </ParentNodeContextConsumer>
    )
}

const FocusableImplementation = (props) => {

    const [currentClassname, setCurrentClassname] = useState(props.classname);
    const ref = useRef(null);
    const parentInternalNodeIdRef = useRef(null);

    const id = useRef(null);
    if (id.current === null)
        id.current = "focusable-" + createUUID();

    const performanceEnhancer = useContext(PerformanceEnhancerContext);

    useEffect(() => {
        parentInternalNodeIdRef.current = document.getElementById(props.parentNodeId);
        return () => {
            id.current = null;
        }
    }, []);

    useEffect(() => {
        if (props.focused) {
            setCurrentClassname(props.focusedClassname);
            const debouncedAnimation = debounce(performAnimation, props.gridAnimationDelay);
            debouncedAnimation();
        } else setCurrentClassname(props.classname);
    }, [props.focused]);

    useEffect(() => {
        if (props.classname !== currentClassname) {
            setCurrentClassname(props.classname);
        }
    }, [props.classname]);

    const performAnimation = () => {
        if (props.fixed) {
            const scrollToObject = props.type === 'horizontal' ? {
                x: '#' + id.current,
                offsetX: props.scrollOffset,
                autoKill: false
            } : {
                y: '#' + id.current,
                offsetY: props.scrollOffset,
                autoKill: false
            }
            gsap.to(parentInternalNodeIdRef.current, {
                duration: props.animationDuration,
                scrollTo: scrollToObject
            });
        } else {
            const boundingElement = ref.current.getBoundingClientRect();
            const boundingContainer = parentInternalNodeIdRef.current.getBoundingClientRect();
            let isInView = false;
            if (props.type === 'vertical') {
                isInView = !((boundingContainer.bottom < boundingElement.bottom) || (boundingContainer.top > boundingElement.top))
            } else if (props.type === 'horizontal') {
                isInView = !((boundingContainer.right < boundingElement.right) || (boundingContainer.left > boundingElement.left))
            }
            if (!isInView) {
                let scrollToObject = null;
                let offset = 0;
                if (props.type === 'horizontal') {
                    if (spatial.direction === 'right') {
                        offset = parentInternalNodeIdRef.current.getBoundingClientRect().width - document.getElementById(id.current).getBoundingClientRect().width - props.scrollOffset
                    }
                    else if (spatial.direction === 'left') {
                        offset = props.scrollOffset
                    }
                    scrollToObject = {
                        x: '#' + id.current,
                        offsetX: offset,
                        autoKill: false
                    }
                }
                else {
                    if (spatial.direction === 'down') {
                        offset = parentInternalNodeIdRef.current.getBoundingClientRect().height - document.getElementById(id.current).getBoundingClientRect().height - props.scrollOffset
                    }
                    else if (spatial.direction === 'up') {
                        offset = props.scrollOffset
                    }
                    scrollToObject = {
                        y: '#' + id.current,
                        offsetY: offset,
                        autoKill: false
                    }
                }
                gsap.to(parentInternalNodeIdRef.current, {
                    duration: props.animationDuration,
                    scrollTo: scrollToObject,
                    force3D: true
                });
            }
        }
    }

    return <div className={`${currentClassname} ${performanceEnhancer.enhancePerformance ? `enhance-performace` : ``}`} id={id.current} ref={ref} >{props.children}</div>;
}

const Focusable = withFocusable({
    trackChildren: false,
})(FocusableImplementation);

AntaresFocusable.defaultProps = {
    onFocus: () => { },
    onBlur: () => { },
    onEnterDown: () => { },
    stealFocus: false,
    onViewportEnter: () => { },
    onViewportExit: () => { },
    scrollOffset: 0,
    animationDuration: 0.5,
    retainLastFocus: false,
    isFocusable: true,
};

AntaresFocusable.propTypes = {
    /** function executed when the item is focused. It takes index of the focused element, the node element and the last direction.*/
    onFocus: PropTypes.func,
    /** function executed when the item is blurred. It takes index of the blurred element, the node element and the last direction.*/
    onBlur: PropTypes.func,
    /** function executed when an enter keydown is performed and the item is focused. */
    onEnterDown: PropTypes.func,
    /** the classname you want to associate to the item when it is NOT focused. */
    classname: PropTypes.string,
    /** the classname you want to associate to the item when it is FOCUSED. */
    focusedClassname: PropTypes.string,
    /** focusable id. It's useful when imperative focus needs to be used. */
    focusableId: PropTypes.string,
    /** index of child element. This is required. */
    index: PropTypes.number.isRequired,
    /** callback that is called when element is in viewport. */
    onViewportEnter: PropTypes.func,
    /** callback that is called when element exits the viewport. */
    onViewportExit: PropTypes.func
}

export default AntaresFocusable;



